/**
 * Form Default Values Utilities
 * Handles smart defaults, last-used values, and date helpers
 */

/**
 * Get today's date in YYYY-MM-DD format
 */
export const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Get the last used cost center for a given type
 */
export const getLastUsedCostCenter = (typeId: string): string => {
  if (!typeId) return '';
  try {
    const key = `lastCostCenter_${typeId}`;
    return localStorage.getItem(key) || '';
  } catch (error) {
    console.error('Error getting last cost center:', error);
    return '';
  }
};

/**
 * Save the last used cost center for a type
 */
export const saveLastUsedCostCenter = (typeId: string, costCenterId: string): void => {
  if (!typeId || !costCenterId) return;
  try {
    const key = `lastCostCenter_${typeId}`;
    localStorage.setItem(key, costCenterId);
  } catch (error) {
    console.error('Error saving last cost center:', error);
  }
};

/**
 * Get the last used category for a given cost center
 */
export const getLastUsedCategory = (costCenterId: string): string => {
  if (!costCenterId) return '';
  try {
    const key = `lastCategory_${costCenterId}`;
    return localStorage.getItem(key) || '';
  } catch (error) {
    console.error('Error getting last category:', error);
    return '';
  }
};

/**
 * Save the last used category for a cost center
 */
export const saveLastUsedCategory = (costCenterId: string, categoryId: string): void => {
  if (!costCenterId || !categoryId) return;
  try {
    const key = `lastCategory_${costCenterId}`;
    localStorage.setItem(key, categoryId);
  } catch (error) {
    console.error('Error saving last category:', error);
  }
};

/**
 * Get the last used shop/vendor for a given category
 */
export const getLastUsedShop = (categoryId: string): string => {
  if (!categoryId) return '';
  try {
    const key = `lastShop_${categoryId}`;
    return localStorage.getItem(key) || '';
  } catch (error) {
    console.error('Error getting last shop:', error);
    return '';
  }
};

/**
 * Save the last used shop/vendor for a category
 */
export const saveLastUsedShop = (categoryId: string, shop: string): void => {
  if (!categoryId || !shop) return;
  try {
    const key = `lastShop_${categoryId}`;
    localStorage.setItem(key, shop);
  } catch (error) {
    console.error('Error saving last shop:', error);
  }
};

/**
 * Get smart defaults for form initialization
 */
export const getSmartDefaults = (
  typeId?: string,
  costCenterId?: string,
  categoryId?: string
): {
  date: string;
  costCenter: string;
  category: string;
  shop: string;
} => {
  return {
    date: getTodayDate(),
    costCenter: typeId ? getLastUsedCostCenter(typeId) : '',
    category: costCenterId ? getLastUsedCategory(costCenterId) : '',
    shop: categoryId ? getLastUsedShop(categoryId) : ''
  };
};

/**
 * Save all last-used values after successful form submission
 */
export const saveLastUsedValues = (data: {
  type?: string;
  costCenter?: string;
  category?: string;
  shop?: string;
}): void => {
  if (data.type && data.costCenter) {
    saveLastUsedCostCenter(data.type, data.costCenter);
  }
  if (data.costCenter && data.category) {
    saveLastUsedCategory(data.costCenter, data.category);
  }
  if (data.category && data.shop) {
    saveLastUsedShop(data.category, data.shop);
  }
};

/**
 * Clear all last-used values (for testing or reset)
 */
export const clearLastUsedValues = (): void => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (
        key.startsWith('lastCostCenter_') ||
        key.startsWith('lastCategory_') ||
        key.startsWith('lastShop_')
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing last-used values:', error);
  }
};
